// src/services/locationCache.js
//
// In-memory cache for the Locations client. Per CONTRACT.md:
//   - 5-minute TTL on getLocations() and getLocation(id)
//   - Detail rows are cached only when getLocation() actually fetches
//     them — never warmed from list rows (those omit child arrays).
//   - Any write through the client invalidates the entire cache
//
// The "full bust on write" rule is the conscious choice for a portfolio
// of < 100 sites: simple and provably correct. If perf becomes an issue
// (it won't at this size) we can move to per-key invalidation later.
//
// Cache keys:
//   "portfolio"      -> Location[]              (result of getLocations)
//   "loc:<id>"       -> Location | null         (result of getLocation)
//
// Module-level singleton. Tests reset state via clear() in beforeEach.

const TTL_MS = 5 * 60 * 1000; // 5 minutes per CONTRACT.md

/** @type {Map<string, { value: any, expiresAt: number }>} */
const store = new Map();

/** Get a cache entry. Returns undefined on miss or expired entry. */
export function get(key) {
  const entry = store.get(key);
  if (!entry) return undefined;
  if (Date.now() >= entry.expiresAt) {
    store.delete(key);
    return undefined;
  }
  return entry.value;
}

/** Set a cache entry. TTL is fixed per CONTRACT.md. */
export function set(key, value) {
  store.set(key, { value, expiresAt: Date.now() + TTL_MS });
}

/**
 * Drop every entry. Called by every successful write in locationsClient.
 * Bluntness is the point - see module header.
 */
export function invalidateAll() {
  store.clear();
}

/** Test-only: reset state. Same as invalidateAll, named for clarity. */
export function __resetForTests() {
  store.clear();
}

/** Test-only: peek at the underlying map size. */
export function __sizeForTests() {
  return store.size;
}

export const PORTFOLIO_KEY = "portfolio";
export const locationKey = (id) => `loc:${id}`;
export const TTL_MS_FOR_TESTS = TTL_MS;
